import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import BookingGetters from "../../store/onlineBooking/getters";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxFooter from "./components/ShareboxFooter";

type Props = {
  token: string;
  email: string;
  onVerified: () => void;
  onBack?: () => void;
};

const OTP_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 15;

export default function D7Otp({ token, email, onVerified, onBack }: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || resending || submitting || !token) return;
    setResending(true);
    setResendNotice(null);
    try {
      await BookingApi.sendShareboxOtpEmail(token);
      setResendNotice({
        text: translate("A new code has been sent."),
        error: false,
      });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setResendNotice({
        text:
          err?.response?.data?.reason ||
          translate("Unable to resend the code. Please try again."),
        error: true,
      });
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (!token) {
        setVerified(true);
        setTimeout(onVerified, 2000);
        return;
      }
      const response = await BookingApi.verifyShareboxOtp(token, code);
      if (response.data.signed) {
        setVerified(true);
        setTimeout(onVerified, 2000);
      } else {
        setCode("");
        setError(translate("Incorrect code. Please try again."));
      }
    } catch (err: any) {
      setCode("");
      const reason = err?.response?.data?.reason;
      if (!reason || reason === "Wrong OTP") {
        setError(translate("Incorrect code. Please try again."));
      } else {
        setError(reason);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const appendDigit = (digit: string) => {
    if (submitting || code.length >= OTP_LENGTH) return;
    setError("");
    setCode((prev) => (prev + digit).slice(0, OTP_LENGTH));
  };

  const removeDigit = () => {
    if (submitting) return;
    setError("");
    setCode((prev) => prev.slice(0, -1));
  };

  useEffect(() => {
    if (code.length === OTP_LENGTH && !submitting) {
      handleVerify();
    }
  }, [code]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.paper" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={`${translate("Step")} 2 ${translate("of")} 3`}
        onBack={onBack}
      />

      <Box sx={{ maxWidth: 480, mx: "auto", px: 3, py: 3 }}>
        {verified ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 56, color: "success.main", mb: 2 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {translate("Contract is signed successfully")}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {translate("Enter the code")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate("We sent a 4-digit code to")} {email}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                mb: resendNotice ? 1 : 3,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {translate("Didn't receive the code?")}
              </Typography>
              <IconButton
                size="small"
                onClick={handleResend}
                disabled={resendCooldown > 0 || resending || submitting}
                aria-label={translate("Resend code")}
                sx={{
                  color: resendCooldown > 0 ? "text.disabled" : "primary.main",
                }}
              >
                {resending ? (
                  <CircularProgress size={16} />
                ) : (
                  <ReplayIcon fontSize="small" />
                )}
              </IconButton>
              {resendCooldown > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {resendCooldown}s
                </Typography>
              )}
            </Box>
            {resendNotice && (
              <Typography
                variant="body2"
                align="center"
                sx={{
                  mb: 3,
                  color: resendNotice.error ? "error.main" : "success.main",
                }}
              >
                {resendNotice.text}
              </Typography>
            )}

            <Box sx={{ position: "relative", mb: 3 }}>
              <input
                autoFocus
                aria-label={translate("Verification code")}
                value={code}
                disabled={submitting}
                onChange={(e) =>
                  setCode(
                    e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH)
                  )
                }
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  left: 0,
                  top: 0,
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                {Array.from({ length: OTP_LENGTH }).map((_, i) => {
                  const isNext = i === code.length;
                  return (
                    <Box
                      key={i}
                      sx={{
                        width: 48,
                        height: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 1.5,
                        border: "2px solid",
                        borderColor: isNext ? "primary.main" : "divider",
                        fontSize: 24,
                        fontWeight: 700,
                        color: "#19344F",
                      }}
                    >
                      {code[i] || ""}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* On-screen numeric keypad: 1-9, blank, 0, backspace */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1.5,
                mb: 2,
              }}
            >
              {[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "",
                "0",
                "backspace",
              ].map((key, idx) => {
                if (key === "") return <Box key={idx} />;
                const isBackspace = key === "backspace";
                return (
                  <Button
                    key={idx}
                    onClick={() =>
                      isBackspace ? removeDigit() : appendDigit(key)
                    }
                    disabled={
                      submitting || (!isBackspace && code.length >= OTP_LENGTH)
                    }
                    sx={{
                      height: 64,
                      fontSize: 22,
                      fontWeight: 700,
                      bgcolor: "grey.100",
                      color: "text.primary",
                      "&:hover": { bgcolor: "grey.200" },
                    }}
                  >
                    {isBackspace ? <BackspaceOutlinedIcon /> : key}
                  </Button>
                );
              })}
            </Box>

            {submitting && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {translate("Verifying OTP...")}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                align="center"
                sx={{ color: "primary.main", fontWeight: 700 }}
              >
                {translate(
                  "By entering the code, you sign the contract and accept the rental terms."
                )}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <ShareboxFooter />
    </Box>
  );
}
